import React, { useState, useEffect } from 'react';
import './App.css';
import TaskList from './TaskList';
import Toolbar from './Toolbar';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import Timeline from './Timeline';
import { ScheduledTask } from './Timeline';
import TaskModal from './TaskModal';
import EditTaskModal from './EditTaskModal'; // new import

type Priority = 'low' | 'medium' | 'high';
type Status = 'pending' | 'inProgress' | 'done';
type Category = 'active' | 'holding' | 'overdue' | 'inactive';

const USER_ID = '1'; // Hardcoded user ID for now

const sendCreateTaskRequest = async (task: TaskCreateRequest): Promise<Task> => {
  // Convert request to valid JSON request including category conversion.
  // For category:
  //   active = 0, holding = 1, overdue = 2, inactive = 3.
  const jsonTaskCreateRequest = {
    title: task.title,
    description: task.description,
    estimated_length: task.estimatedLength,
    due_date: task.dueDate.getTime() / 1000,
    priority: task.priority === 'low' ? 1 : task.priority === 'medium' ? 2 : 3,
    status: task.status === 'pending' ? 0 : task.status === 'inProgress' ? 1 : 2,
    category: task.category === 'active' ? 0 : task.category === 'holding' ? 1 : task.category === 'overdue' ? 2 : 3,
    resources: task.resources,
  };

  const response = await fetch('https://api.omara.us/tasks', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(jsonTaskCreateRequest),
  });

  if (!response.ok) {
    throw new Error('Failed to create task');
  }

  const createdTask = await response.json();

  return {
    id: createdTask.id,
    ...task,
  };
};

const sendDeleteTaskRequest = async (taskId: number) => {
  // Send request to server
  const response = await fetch(`https://api.omara.us/tasks/${taskId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    throw new Error('Failed to delete task');
  }
}

const droppableIdToCategory = (droppableId: string): Category => {
  switch (droppableId) {
    case 'taskPool':
      return 'active';
    case 'holding':
      return 'holding';
    case 'overdue':
      return 'overdue';
    default:
      return 'inactive';
  }
}

const sendUpdateTaskRequest = async (task: Task): Promise<Task> => {
  // Convert request to valid JSON request including category conversion.
  // For category:
  //   active = 0, holding = 1, overdue = 2, inactive = 3.
  const jsonTaskUpdateRequest = {
    id: task.id,
    title: task.title,
    description: task.description,
    estimated_length: task.estimatedLength,
    due_date: task.dueDate.getTime() / 1000,
    priority: task.priority === 'low' ? 1 : task.priority === 'medium' ? 2 : 3,
    status: task.status === 'pending' ? 0 : task.status === 'inProgress' ? 1 : 2,
    category: task.category === 'active' ? 0 : task.category === 'holding' ? 1 : task.category === 'overdue' ? 2 : 3,
    resources: task.resources,
  };

  const response = await fetch(`https://api.omara.us/tasks/${task.id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(jsonTaskUpdateRequest),
  });

  if (!response.ok) {
    throw new Error('Failed to update task');
  }

  return task;
}

const sendUpdateRankingsRequest = async (userId: string, rankings: Rankings) => {
  const response = await fetch(`https://api.omara.us/rankings/${userId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(rankings),
  });

  if (!response.ok) {
    throw new Error('Failed to update rankings');
  }
}

const fetchRankings = async (userId: string): Promise<Rankings> => {
  const response = await fetch(`https://api.omara.us/rankings/${userId}`, {
    method: 'GET',
  }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch rankings');
  }
  console.log("FROM fetchRankings: ", response);
  return response.json();
}

// Rankings are stored in the backend as an array of task IDs for each category
type Rankings = {
  user_id: number; // User ID
  active: number[];
  holding: number[];
  overdue: number[];
  inactive: number[];
};

type TaskReadResponse = {
  id: number;
  title: string;
  description: string;
  estimated_length: number;
  due_date: number;
  priority: number;
  status: number;
  category: number; // new field from backend
  resources: string[];
};

type Task = {
  // Required fields
  id: number; // Generated by database on creation
  title: string;
  description: string;
  estimatedLength: number;
  dueDate: Date;

  // Optional fields
  elapsedTime?: number;
  priority?: Priority;
  status?: Status;
  category: Category; // new property
  resources?: string[];
};

type TaskCreateRequest = Omit<Task, 'id'>;

type TaskState = {
  taskPool: Task[];
  holding: Task[];
  overdue: Task[];
};

const initialTasks: TaskState = {
  taskPool: [],
  holding: [],
  overdue: [],
};

const initialScheduledTasks: ScheduledTask[] = [];

const sortTasksByRanking = (tasksArray: Task[], ranking: number[]): Task[] => {
  return tasksArray.slice().sort((a, b) => {
    const aRank = ranking.indexOf(a.id);
    const bRank = ranking.indexOf(b.id);
    if (aRank === -1 && bRank === -1) return 0;
    if (aRank === -1) return 1;
    if (bRank === -1) return -1;
    return aRank - bRank;
  });
};

const App: React.FC = () => {
  const [tasks, setTasks] = useState<TaskState>(initialTasks);
  const [scheduledTasks, setScheduledTasks] = useState<ScheduledTask[]>(initialScheduledTasks);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null); // new state

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const response = await fetch('https://api.omara.us/tasks');
        if (response.ok) {
          const fetchedTasks: TaskReadResponse[] = await response.json();

          // Fetched tasks need to be converted to Task type
          // due_date -> dueDate (Date)
          // estimated_length -> estimatedLength (number)
          // priority -> Priority
          // status -> Status

          const convertedTasks: Task[] = fetchedTasks.map(task => ({
            id: task.id,
            title: task.title,
            description: task.description,
            estimatedLength: task.estimated_length,
            dueDate: new Date(task.due_date * 1000),
            priority: task.priority === 1 ? 'low' : task.priority === 2 ? 'medium' : 'high',
            status: task.status === 0 ? 'pending' : task.status === 1 ? 'inProgress' : 'done',
            category: task.category === 0 ? 'active' : task.category === 1 ? 'holding' : task.category === 2 ? 'overdue' : 'inactive',
            resources: task.resources,
          }));

          console.log('Fetched tasks:', convertedTasks);

          try {
            const rankings = await fetchRankings(USER_ID);
            console.log('Fetched rankings:', rankings);

            // If rankings exist, partition tasks into the appropriate arrays, and sort by rankings
            setTasks({
              taskPool: sortTasksByRanking(convertedTasks.filter(t => t.category === 'active'), rankings.active),
              holding: sortTasksByRanking(convertedTasks.filter(t => t.category === 'holding'), rankings.holding),
              overdue: sortTasksByRanking(convertedTasks.filter(t => t.category === 'overdue'), rankings.overdue),
            });
          } catch (rankingError) {
            console.error('Failed to fetch rankings:', rankingError);
            // If rankings fetch fails, fallback to unsorted tasks.
            setTasks({
              taskPool: convertedTasks.filter(t => t.category === 'active'),
              holding: convertedTasks.filter(t => t.category === 'holding'),
              overdue: convertedTasks.filter(t => t.category === 'overdue'),
            });
          }
        } else {
          console.error('Failed to fetch tasks:', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching tasks:', error);
      }
    };

    fetchTasks();
  }, []);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;

    // Restore timeline functionality:
    if (destination.droppableId === 'timeline') {
      const draggedTask = tasks[source.droppableId as keyof TaskState][source.index];
      const newStart = scheduledTasks.length
        ? scheduledTasks[scheduledTasks.length - 1].endTime
        : 6; // default start time
      const newEnd = newStart + draggedTask.estimatedLength;
      const newScheduledTask = {
        id: draggedTask.id + '-' + Date.now(), // produce unique id
        title: draggedTask.title,
        startTime: newStart,
        endTime: newEnd,
        description: draggedTask.description,
        estimatedLength: draggedTask.estimatedLength,
      };
      setScheduledTasks((prev) => [...prev, newScheduledTask]);
      return;
    }

    // Handle same-droppable reorder:
    if (source.droppableId === destination.droppableId) {
      const list = Array.from(tasks[source.droppableId as keyof TaskState]);
      const [removed] = list.splice(source.index, 1);
      list.splice(destination.index, 0, removed);
      setTasks((prevTasks) => ({
        ...prevTasks,
        [source.droppableId]: list,
      }));

      const updatedRanking: Rankings = {
        user_id: parseInt(USER_ID),
        active: source.droppableId === 'taskPool'
          ? list.map(t => t.id)
          : tasks.taskPool.map(t => t.id),
        holding: source.droppableId === 'holding'
          ? list.map(t => t.id)
          : tasks.holding.map(t => t.id),
        overdue: source.droppableId === 'overdue'
          ? list.map(t => t.id)
          : tasks.overdue.map(t => t.id),
        inactive: [],
      };
      sendUpdateRankingsRequest(USER_ID, updatedRanking);
    } else {
      // Handle cross-list move:
      const sourceList = Array.from(tasks[source.droppableId as keyof TaskState]);
      const destinationList = Array.from(tasks[destination.droppableId as keyof TaskState]);
      const [removed] = sourceList.splice(source.index, 1);
      destinationList.splice(destination.index, 0, removed);
      setTasks((prevTasks) => ({
        ...prevTasks,
        [source.droppableId]: sourceList,
        [destination.droppableId]: destinationList,
      }));

      // Update moved task's category and update rankings for both lists:
      const updatedTask = { ...removed, category: droppableIdToCategory(destination.droppableId) };
      updateTask(updatedTask);

      const newRankings: Rankings = {
        user_id: parseInt(USER_ID),
        active: destination.droppableId === 'taskPool'
          ? destinationList.map(t => t.id)
          : tasks.taskPool.map(t => t.id),
        holding: destination.droppableId === 'holding'
          ? destinationList.map(t => t.id)
          : tasks.holding.map(t => t.id),
        overdue: destination.droppableId === 'overdue'
          ? destinationList.map(t => t.id)
          : tasks.overdue.map(t => t.id),
        inactive: [],
      };
      sendUpdateRankingsRequest(USER_ID, newRankings);
    }
  };

  const createNewTask = async (title: string, description: string, estimatedLength: number, dueDate: Date, priority: Priority, status: Status) => {
    // New tasks default to category 'active'
    const newTaskRequest: TaskCreateRequest = {
      title,
      description,
      estimatedLength,
      dueDate,
      priority,
      status,
      category: 'active',
      resources: [],
    };

    // Send request to server
    const newTask: Task = await sendCreateTaskRequest(newTaskRequest);

    setTasks((prevTasks) => ({
      ...prevTasks,
      taskPool: [...prevTasks.taskPool, newTask],
    }));
  };

  const updateTask = (updatedTask: Task) => {
    // Send request to server
    sendUpdateTaskRequest(updatedTask);

    setTasks(prevTasks => ({
      taskPool: prevTasks.taskPool.map(t => t.id === updatedTask.id ? updatedTask : t),
      holding: prevTasks.holding.map(t => t.id === updatedTask.id ? updatedTask : t),
      overdue: prevTasks.overdue.map(t => t.id === updatedTask.id ? updatedTask : t),
    }));
  };

  const deleteTask = async (task: Task) => {
    // Send request to server
    await sendDeleteTaskRequest(task.id);

    setTasks(prevTasks => ({
      taskPool: prevTasks.taskPool.filter(t => t.id !== task.id),
      holding: prevTasks.holding.filter(t => t.id !== task.id),
      overdue: prevTasks.overdue.filter(t => t.id !== task.id),
    }));
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
  };

  return (
    <div className="App">
      <Toolbar onCreateTask={() => setIsModalOpen(true)} />
      {/* Include Timeline inside the DragDropContext so it can act as drop target */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Timeline startHour={6} endHour={20} tasks={scheduledTasks} onUpdateTasks={setScheduledTasks} />
        <div className="task-lists">
          <TaskList droppableId="taskPool" title="Task Pool" tasks={tasks.taskPool} onEditTask={handleEditTask} />
          <TaskList droppableId="holding" title="Holding" tasks={tasks.holding} onEditTask={handleEditTask} />
          <TaskList droppableId="overdue" title="Overdue" tasks={tasks.overdue} onEditTask={handleEditTask} />
        </div>
      </DragDropContext>
      <TaskModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={createNewTask}
      />
      {editingTask && (
        <EditTaskModal
          isOpen={true}
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSave={updateTask}
          onDelete={deleteTask}
        />
      )}
    </div>
  );
};

export default App;
export type { Task };
